// Global Instructions Rule Applied!
//
// Company form — Documents tab (internal agents §D2). The company's files with
// their document-type classification: uploads a person makes here, and the PDF
// attachments the follower agent ingests off monitored mailboxes (investor
// updates, management accounts). A document traces back to the email it arrived
// on, and its extraction state says whether the agent could read it.
//
// Cloned from the deal Documents tab (deal_execution/components/DocumentsTab)
// minus the requirement tracker, lifecycle and version columns — those are
// deal-execution concepts. Category is a client-side filter only: a file's
// category is derived through its type (no category column on
// file_urls_accounts), so the two can never disagree.
//
// Staleness: the app shell never unmounts tabs (TabContentWrapper hides them
// with display:none), so every mutation refetches and `onRefreshReady` hands
// `refetch` up to the company form so its Refresh button drives this tab too.
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Popconfirm, Select, Spin, Table, Tag } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import UploadModal from '../../../core/components/FileUploadManager/parts/UploadModal.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { addTab } from '../../../store/slices/tabSlice'
import { deleteFile, getSignedUrl, uploadFile } from '../../../core/services/filesService.ts'
import {
  FILE_TYPE,
  OFFICE_EXTENSIONS,
  OFFICE_VIEWER_URL,
  URL_TYPE
} from '../../../state_manager/constants/files.ts'
import { FIELD_ACCOUNT_ID, TABLE_FILE_URLS_ACCOUNTS } from '../../../state_manager/globals.ts'
import {
  AccountDocumentsData,
  classifyAccountFile,
  fetchAccountDocuments
} from '../services/documentsService.ts'

interface AccountDocumentsTabProps {
  accountId: string | null
  /** Hands `refetch` to the company form so its Refresh button reloads this tab. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

// Extraction state of an agent-ingested PDF. 'no_text_layer' is a scanned
// document: it is stored and downloadable, the agent just could not read it.
const EXTRACTION_LABELS: Record<string, { label: string; color: string }> = {
  extracted: { label: 'Read', color: 'green' },
  no_text_layer: { label: 'No text layer', color: 'gold' },
  error: { label: 'Extract failed', color: 'red' }
}

function isMicrosoftOfficeFile(fileName: string): boolean {
  if (!fileName || typeof fileName !== 'string') return false
  const extension = fileName.toLowerCase().split('.').pop()
  return extension ? OFFICE_EXTENSIONS.includes(extension) : false
}

export default function AccountDocumentsTab({
  accountId,
  onRefreshReady = null
}: AccountDocumentsTabProps): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>()
  const token = () => getAccessToken(store.getState().auth)

  const [data, setData] = useState<AccountDocumentsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<Record<string, string | null>>({})

  const refetch = useCallback(async () => {
    if (!accountId) return
    const result = await fetchAccountDocuments(accountId, token())
    if (result.error) showErrorToast(result.error)
    setData(result.data)
    setLoading(false)
  }, [accountId])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  const documentTypes = useMemo(() => data?.document_types ?? [], [data])
  const documentCategories = useMemo(() => data?.document_categories ?? [], [data])
  const files = useMemo(() => data?.files ?? [], [data])

  // The category select narrows the type list; it is never persisted.
  const typeOptionsFor = (fileId: string, currentTypeId: string | null) => {
    const explicit = categoryFilter[fileId]
    const implied =
      explicit === undefined
        ? ((documentTypes.find((t) => t.id === currentTypeId)?.category_id as string | null) ?? null)
        : explicit
    return documentTypes
      .filter((t) => !implied || t.category_id === implied)
      .map((t) => ({ value: t.id, label: t.label }))
  }

  const categoryValueFor = (fileId: string, currentTypeId: string | null) => {
    const explicit = categoryFilter[fileId]
    if (explicit !== undefined) return explicit ?? undefined
    return (documentTypes.find((t) => t.id === currentTypeId)?.category_id as string | null) ?? undefined
  }

  const handleClassify = async (fileId: string, documentTypeId: string | null): Promise<void> => {
    const result = await classifyAccountFile(fileId, documentTypeId, token())
    if (!result.success) showErrorToast(result.error || 'Failed to update classification')
    await refetch()
  }

  const handleUpload = async (uploadData: {
    type: typeof FILE_TYPE | typeof URL_TYPE
    files?: File[]
    url?: string
    fileName?: string
  }): Promise<void> => {
    if (!accountId) return
    setUploading(true)
    try {
      if (uploadData.type === FILE_TYPE && uploadData.files?.length) {
        // Sequential, so one bad file reports on its own rather than
        // collapsing the whole batch into a single failure.
        let failures = 0
        for (const file of uploadData.files) {
          try {
            await uploadFile(
              file, token(), accountId, null, FILE_TYPE, undefined, undefined,
              TABLE_FILE_URLS_ACCOUNTS, FIELD_ACCOUNT_ID
            )
          } catch (e) {
            failures += 1
            showErrorToast(`${file.name}: ${e instanceof Error ? e.message : 'upload failed'}`)
          }
        }
        if (failures < uploadData.files.length) toast.success('File(s) uploaded')
      } else if (uploadData.type === URL_TYPE && uploadData.url && uploadData.fileName) {
        await uploadFile(
          null, token(), accountId, null, URL_TYPE, uploadData.url, uploadData.fileName,
          TABLE_FILE_URLS_ACCOUNTS, FIELD_ACCOUNT_ID
        )
        toast.success('URL added')
      }
      setUploadOpen(false)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (fileId: string): Promise<void> => {
    setDeletingId(fileId)
    try {
      // Row-backed delete: the backend moves the storage object to
      // _trash/{date}/{row_id}/{filename} and trashes the row. Recoverable.
      await deleteFile(null, token(), fileId, TABLE_FILE_URLS_ACCOUNTS)
      toast.success('File deleted')
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to delete file')
    } finally {
      setDeletingId(null)
    }
  }

  const handleOpen = async (file: Record<string, unknown>): Promise<void> => {
    const fileName = (file.file_name as string) || 'File'
    const filePath = (file.file_url as string) || ''
    if (!filePath) return
    try {
      // 'url' rows already hold a public link; storage rows need a short-lived
      // signed URL. Office formats route through the web viewer.
      let urlToOpen =
        file.type === URL_TYPE ? filePath : (await getSignedUrl(filePath, token())).signed_url
      if (isMicrosoftOfficeFile(fileName)) {
        urlToOpen = `${OFFICE_VIEWER_URL}${encodeURIComponent(urlToOpen)}`
      }
      const tabKey = `file-viewer-${filePath.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
      dispatch(
        addTab({
          id: tabKey,
          key: tabKey,
          title: fileName,
          path: `/file-viewer/${tabKey}`,
          type: 'file-viewer',
          module: 'file-viewer',
          closeable: true,
          fileUrl: urlToOpen,
          fileName
        })
      )
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to open file')
    }
  }

  if (loading || !data) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'file_name',
      render: (v: unknown, row: Record<string, unknown>) => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
          onClick={() => handleOpen(row)}
        >
          {(v as string) || '—'}
        </button>
      )
    },
    {
      title: 'Category',
      dataIndex: 'document_category_id',
      width: 200,
      render: (_v: unknown, row: Record<string, unknown>) => (
        <Select
          size='small'
          allowClear
          className='w-full'
          placeholder='All'
          value={categoryValueFor(row.id as string, (row.document_type_id as string) ?? null)}
          onChange={(value) => {
            setCategoryFilter((prev) => ({ ...prev, [row.id as string]: value ?? null }))
            const current = documentTypes.find((t) => t.id === row.document_type_id)
            if (value && current && current.category_id !== value) {
              handleClassify(row.id as string, null)
            }
          }}
          options={documentCategories.map((c) => ({ value: c.id, label: c.label }))}
        />
      )
    },
    {
      title: 'Type',
      dataIndex: 'document_type_id',
      width: 230,
      render: (v: unknown, row: Record<string, unknown>) => (
        <Select
          size='small'
          allowClear
          className='w-full'
          placeholder='Classify…'
          value={(v as string) ?? undefined}
          onChange={(value) => handleClassify(row.id as string, value ?? null)}
          options={typeOptionsFor(row.id as string, (v as string) ?? null)}
        />
      )
    },
    {
      title: 'Source',
      dataIndex: 'source_interaction_subject',
      width: 240,
      render: (v: unknown, row: Record<string, unknown>) =>
        row.source_interaction_id ? (
          <span className='text-xs text-gray-600'>
            {(v as string) || 'Email'}
            {row.source_interaction_datetime
              ? ` · ${dayjs(String(row.source_interaction_datetime)).format('MM/DD/YYYY')}`
              : ''}
          </span>
        ) : (
          <span className='text-xs text-gray-400'>Uploaded</span>
        )
    },
    {
      title: 'Extraction',
      dataIndex: 'extraction_status',
      width: 130,
      render: (v: unknown) => {
        const state = EXTRACTION_LABELS[v as string]
        return state ? <Tag color={state.color}>{state.label}</Tag> : <span className='text-gray-400'>—</span>
      }
    },
    {
      title: 'Added',
      dataIndex: 'date_created',
      width: 110,
      render: (v: unknown) => (v ? dayjs(String(v)).format('MM/DD/YYYY') : '—')
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 50,
      render: (_v: unknown, row: Record<string, unknown>) => (
        <Popconfirm
          title='Delete this file?'
          okText='Delete'
          cancelText='Cancel'
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(row.id as string)}
        >
          <Button
            size='small'
            type='text'
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === row.id}
          />
        </Popconfirm>
      )
    }
  ]

  return (
    <div className='px-2 py-4 space-y-4'>
      <FormSection
        title='Files'
        titleColor='green'
        scrollable={false}
        headerActions={
          <span className='flex items-center gap-2'>
            <Button size='small' icon={<ReloadOutlined />} onClick={refetch}>
              Refresh
            </Button>
            <Button
              size='small'
              type='primary'
              icon={<UploadOutlined />}
              onClick={() => setUploadOpen(true)}
              disabled={!accountId}
            >
              Upload
            </Button>
          </span>
        }
      >
        {files.length === 0 ? (
          <div className='text-sm text-gray-400 py-3'>No documents for this company yet.</div>
        ) : (
          <Table
            size='small'
            rowKey='id'
            pagination={false}
            columns={columns}
            dataSource={files as Record<string, unknown>[]}
          />
        )}
      </FormSection>

      <UploadModal
        visible={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onUpload={handleUpload}
        loading={uploading}
      />
    </div>
  )
}
